import '@/assets/styles/components/form-input.styles.scss';
import React, { useState, useEffect, forwardRef } from 'react';
import { Eye, EyeClosed, Dot, X, Check } from 'lucide-react';
import { FormInputProps } from './form-input.types.ts';
import { passwordRequirements } from './form-input.schema.ts';

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
	({ label, variant = 'text', value = '', onChange, placeholder, className = '', error = false, helperText, showRequirements = false, ...props }, ref) => {
		const [showPassword, setShowPassword] = useState(false);
		const [requirements, setRequirements] = useState<{
			[key: string]: boolean;
		}>({});

		useEffect(() => {
			if (variant === 'password') {
				const newRequirements = passwordRequirements.reduce(
					(acc, req) => ({
						...acc,
						[req.id]: req.validator(value as string)
					}),
					{}
				);
				setRequirements(newRequirements);
			}
		}, [value, variant]);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			if (variant === 'password') {
				const newValue = e.target.value.replace(/\D/g, '').slice(0, 6);
				const modifiedEvent = {
					...e,
					target: {
						...e.target,
						value: newValue
					}
				};
				onChange?.(modifiedEvent);
			} else if (variant === 'number') {
				const newValue = e.target.value.replace(/[^0-9]/g, '');
				const modifiedEvent = {
					...e,
					target: {
						...e.target,
						value: newValue
					}
				};
				onChange?.(modifiedEvent);
			} else if (variant === 'iban') {
				const newValue = e.target.value.replace(/[^0-9]/g, '');
				const modifiedEvent = {
					...e,
					target: {
						...e.target,
						value: newValue
					}
				};
				onChange?.(modifiedEvent);
			} else {
				onChange?.(e);
			}
		};

		const getInputType = () => {
			if (variant === 'password' && !showPassword) {
				return 'password';
			} else if (variant === 'password' || variant === 'number' || variant === 'iban') {
				return 'text';
			} else {
				return 'text';
			}
		};

		const getInputModeAndPattern = () => {
			if (variant === 'password' || variant === 'number' || variant === 'iban') {
				return {
					inputMode: 'numeric' as React.HTMLAttributes<HTMLInputElement>['inputMode'],
					pattern: '[0-9]*'
				};
			}
			return {};
		};

		return (
			<div className="form-group">
				{label && <h3>{label}</h3>}
				<div className={`input-wrapper ${variant === 'iban' ? 'iban-wrapper' : ''}`}>
					{variant === 'iban' && <div className={`iban-prefix ${value ? 'has-value' : ''}`}>TR</div>}
					<input
						ref={ref}
						type={getInputType()}
						value={value}
						onChange={handleChange}
						className={`${className} ${error ? 'error' : ''} ${variant === 'iban' ? 'iban-input' : ''}`}
						placeholder={placeholder}
						{...getInputModeAndPattern()}
						{...props}
					/>
					{variant === 'password' && (
						<button
							type="button"
							className="toggle-password"
							onClick={() => setShowPassword(!showPassword)}>
							{showPassword ? <Eye size={20} /> : <EyeClosed size={20} />}
						</button>
					)}
				</div>

				{helperText && error && <div className="error-text">{helperText}</div>}

				{variant === 'password' && showRequirements && (
					<div className="password-requirements">
						{passwordRequirements.map(req => (
							<div
								key={req.id}
								className={`requirement ${value && requirements[req.id] ? 'valid' : value ? 'invalid' : ''}`}>
								{value && requirements[req.id] ? <Check size={16} /> : value ? <X size={16} /> : <Dot size={16} />}
								{req.text}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}
);

FormInput.displayName = 'FormInput';
